import React, { FC, useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import GreenLandScore, { IconText } from '@loomstateorg/shared-frontend/components/commercetools-ui/organisms/greenland-score';
import { useFormat } from '@loomstateorg/shared-frontend/hooks/useFormat';

interface SustainabilityBadgeProps {
  iconsWithText: IconText[];
  environmentalTextColor?: string;
  className?: string;
  tooltipClassName?: string;
  index?: number;
}

const METRIC_DESCRIPTIONS: Record<string, string> = {
  ghg: 'Estimated greenhouse gas emissions',
  water: 'Estimated water consumption',
  land: 'Estimated land use',
};

const METRIC_FAMILIES: [RegExp, string][] = [
  [/ghg|co2|emission/, 'ghg'],
  [/water/, 'water'],
  [/land/, 'land'],
];

const toMetricSlug = (attributeKey?: string) => {
  const normalized = (attributeKey ?? '')
    .toLowerCase()
    .replace(/^impacts-?/, '')
    .replace(/[^a-z0-9]/g, '');

  return METRIC_FAMILIES.find(([pattern]) => pattern.test(normalized))?.[1] ?? normalized;
};

type TooltipPlacement = 'above' | 'below';

const SustainabilityBadge: FC<SustainabilityBadgeProps> = ({
  iconsWithText,
  environmentalTextColor,
  className = '',
  tooltipClassName = 'fixed z-[9999] w-max max-w-240 rounded border border-gray-300 bg-site p-12 text-left shadow-lg',
  index = 0,
}) => {
  const { formatMessage } = useFormat({ name: 'product' });
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const tooltipId = useId();
  const triggerRef = useRef<HTMLDivElement>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number; placement: TooltipPlacement } | null>(
    null,
  );

  const updateTooltipPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const estimatedHeight = 220;
    const showAbove = rect.bottom + estimatedHeight > window.innerHeight;

    setTooltipPosition({
      top: showAbove ? rect.top - 8 : rect.bottom + 8,
      left: rect.left,
      placement: showAbove ? 'above' : 'below',
    });
  }, []);

  const showTooltip = useCallback(() => {
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    setIsTooltipVisible(true);
  }, []);

  const hideTooltip = useCallback(() => {
    hideTimeoutRef.current = setTimeout(() => setIsTooltipVisible(false), 80);
  }, []);

  const hideTooltipImmediately = useCallback(() => {
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    setIsTooltipVisible(false);
  }, []);

  useEffect(
    () => () => {
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    },
    [],
  );

  useLayoutEffect(() => {
    if (!isTooltipVisible) {
      setTooltipPosition(null);
      return;
    }

    updateTooltipPosition();
    window.addEventListener('scroll', updateTooltipPosition, true);
    window.addEventListener('resize', updateTooltipPosition);

    return () => {
      window.removeEventListener('scroll', updateTooltipPosition, true);
      window.removeEventListener('resize', updateTooltipPosition);
    };
  }, [isTooltipVisible, updateTooltipPosition]);

  const metrics = useMemo(() => iconsWithText.filter((metric) => !isNaN(parseFloat(metric.text))), [iconsWithText]);

  if (!metrics.length) return null;

  const tooltipContent = (
    <div className="font-primaryThin text-12 font-normal leading-loose">
      <p className="pb-8 font-semibold">
        {formatMessage({ id: 'sustainability.tooltip.title', defaultMessage: 'What do these values mean?' })}
      </p>
      <ul className="flex flex-col gap-6">
        {metrics.map((metric, index) => {
          const slug = toMetricSlug(metric.attributeKey);
          const label = metric.label?.trim() || slug.toUpperCase();
          const description = formatMessage({
            id: `sustainability.metric.${slug}`,
            defaultMessage: METRIC_DESCRIPTIONS[slug] ?? '',
          });

          return (
            <li key={`${slug}-${index}`} className="flex items-center gap-6">
              <span className="flex size-14 shrink-0 items-center justify-center">{metric.icon}</span>
              <span>{description ? `${label} – ${description}` : label}</span>
            </li>
          );
        })}
      </ul>
      <p className="pt-8">
        {formatMessage({
          id: 'sustainability.tooltip.footer',
          defaultMessage: 'Calculated using standardized sustainability metrics.',
        })}
      </p>
    </div>
  );

  const tooltipNode =
    isTooltipVisible &&
    tooltipPosition &&
    typeof document !== 'undefined' &&
    createPortal(
      <div
        id={tooltipId}
        role="tooltip"
        className={tooltipClassName}
        data-testid={`sustainability-tooltip-${index}`}
        style={{
          top: tooltipPosition.top,
          left: tooltipPosition.left,
          transform: tooltipPosition.placement === 'above' ? 'translateY(-100%)' : undefined,
        }}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
      >
        {tooltipContent}
      </div>,
      document.body,
    );

  return (
    <div className={className} data-testid={`sustainability-badge-${index}`}>
      <div
        ref={triggerRef}
        role="group"
        tabIndex={0}
        aria-label={formatMessage({ id: 'sustainability.badge.label', defaultMessage: 'Sustainability impact' })}
        aria-describedby={isTooltipVisible ? tooltipId : undefined}
        className="inline-flex cursor-help items-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-black"
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltipImmediately}
        onKeyDown={(event) => {
          if (event.key === 'Escape') hideTooltipImmediately();
        }}
      >
        <GreenLandScore iconsWithText={iconsWithText} environmentalTextColor={environmentalTextColor} />
      </div>

      {tooltipNode}
    </div>
  );
};

export default SustainabilityBadge;
